import { sysColors } from "@/assets/theme/colors";
import { formatDayLabel } from "../_utils";
import type {
  CareerServicesCategoryStat,
  CareerServicesInteractionStats,
  CareerServicesModeStat,
  CareerServicesOwnerStat,
  CareerServicesRecentInteraction
} from "./queries";

export const UNASSIGNED_OWNER_LABEL = "Unassigned";

export type CareerServicesDayTotal = {
  date: string;
  total: number;
};

export type FilteredCareerServicesStats = {
  total: number;
  byMode: CareerServicesModeStat[];
  categories: CareerServicesCategoryStat[];
  days: CareerServicesDayTotal[];
  recentInteractions: CareerServicesRecentInteraction[];
  topCategory: CareerServicesCategoryStat | null;
  topMode: string | null;
  activeDays: number;
};

/**
 * Slice career stats to the selected categories and mode. Totals come from the
 * mode slice (interactions carry multiple categories, so category counts don't sum).
 * An explicitly empty category selection matches nothing, mirroring membership.
 */
export function filterCareerServicesStats(
  stats: CareerServicesInteractionStats | null | undefined,
  selectedCategories: string[],
  selectedMode: string | null
): FilteredCareerServicesStats {
  if (selectedCategories.length === 0) {
    return {
      total: 0,
      byMode: [],
      categories: [],
      days: [],
      recentInteractions: [],
      topCategory: null,
      topMode: null,
      activeDays: 0
    };
  }
  const selectedSet = new Set(selectedCategories);
  const byMode = (stats?.byMode ?? []).filter((entry) => !selectedMode || entry.mode === selectedMode);
  const categories = (stats?.categories ?? []).filter((entry) => selectedSet.has(entry.category));
  const days = (stats?.days ?? []).map((day) => ({
    date: day.date,
    total: selectedMode ? (day.modes.find((entry) => entry.mode === selectedMode)?.count ?? 0) : day.total
  }));
  const total = selectedMode
    ? (stats?.byMode.find((entry) => entry.mode === selectedMode)?.count ?? 0)
    : (stats?.totalInteractions ?? 0);
  const recentInteractions = (stats?.recentInteractions ?? []).filter(
    (interaction) =>
      (!selectedMode || interaction.mode === selectedMode) &&
      interaction.categories.some((category) => selectedSet.has(category))
  );
  const topCategory = categories.reduce<CareerServicesCategoryStat | null>(
    (top, entry) => (!top || entry.count > top.count ? entry : top),
    null
  );
  const topModeEntry = (stats?.byMode ?? []).reduce<CareerServicesModeStat | null>(
    (top, entry) => (!top || entry.count > top.count ? entry : top),
    null
  );

  return {
    total,
    byMode,
    categories,
    days,
    recentInteractions,
    topCategory: topCategory && topCategory.count > 0 ? topCategory : null,
    topMode: selectedMode ?? (topModeEntry && topModeEntry.count > 0 ? topModeEntry.mode : null),
    activeDays: days.filter((day) => day.total > 0).length
  };
}

export type CareerBarRow = {
  name: string;
  Interactions: number;
};

export function buildModeBarData(byMode: CareerServicesModeStat[]): CareerBarRow[] {
  return [...byMode]
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count)
    .map((entry) => ({ name: entry.mode, Interactions: entry.count }));
}

export function buildCategoryBarData(categories: CareerServicesCategoryStat[]): CareerBarRow[] {
  return [...categories]
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count)
    .map((entry) => ({ name: entry.category, Interactions: entry.count }));
}

export type CareerTrendRow = {
  name: string;
  Interactions: number;
};

export type CareerTrendSeries = {
  dataKey: string;
  name: string;
  color: string;
};

export function buildVolumeTrendData(days: CareerServicesDayTotal[]): {
  rows: CareerTrendRow[];
  series: CareerTrendSeries[];
} {
  return {
    rows: days.map((day) => ({ name: formatDayLabel(day.date), Interactions: day.total })),
    series: [{ dataKey: "Interactions", name: "Interactions", color: sysColors["sys-brand-primary"] }]
  };
}

export function interactionPathForCareerInteraction(interaction: CareerServicesRecentInteraction): string | null {
  if (!interaction.personPgaId) return null;
  return `/people/${interaction.personPgaId}/interactions/interactions/${interaction.id}`;
}

export function ownerDisplayName(ownerName: string | null): string {
  return ownerName ?? UNASSIGNED_OWNER_LABEL;
}

export type OwnerOption = {
  value: string;
  label: string;
};

/**
 * Named owners only — the Unassigned bucket has no owner id to filter by,
 * so it appears on the leaderboard but never as a filter choice.
 */
export function buildOwnerOptions(owners: CareerServicesOwnerStat[]): OwnerOption[] {
  return owners
    .filter((entry) => entry.ownerId != null)
    .map((entry) => ({
      value: entry.ownerId as string,
      label: `${ownerDisplayName(entry.ownerName)} (${entry.interactions})`
    }));
}

/** Mean interactions per named team member; Unassigned rows are not a member. */
export function teamAverageInteractions(owners: CareerServicesOwnerStat[]): number | null {
  const named = owners.filter((entry) => entry.ownerId != null);
  if (named.length === 0) return null;
  return named.reduce((sum, entry) => sum + entry.interactions, 0) / named.length;
}

export function teamTotalInteractions(owners: CareerServicesOwnerStat[]): number {
  return owners.reduce((sum, entry) => sum + entry.interactions, 0);
}

export function formatSignedCount(value: number | null): string {
  if (value == null) return "—";
  const rounded = Math.round(value);
  if (rounded === 0) return "±0";
  return rounded < 0 ? `−${Math.abs(rounded)}` : `+${rounded}`;
}

/** Member volume against the team average, e.g. "+3 vs avg". */
export function interactionsDeltaText(memberCount: number | null, teamAvg: number | null): string {
  if (memberCount == null || teamAvg == null) return "—";
  return `${formatSignedCount(memberCount - teamAvg)} vs avg`;
}

/** Bar fill for the leaderboard share column, relative to the busiest member. */
export function ownerShareOfMax(interactions: number, maxInteractions: number): number {
  if (maxInteractions <= 0) return 0;
  return Math.min(1, Math.max(0, interactions / maxInteractions));
}
